import { type Editor } from '@tiptap/react';

import { Separator } from '../../../primitives/separator';

import { ButtonDivider } from '../commands/button-divider';
import { ButtonEraser, ERASER_TYPE } from '../commands/button-eraser';
import { ButtonHistory } from '../commands/button-history';
import { ButtonImage, type MediaPickerComponent } from '../commands/button-image';
import { ComboboxCommands } from '../commands/list/combobox-commands';
import { ComboboxText } from '../commands/list/combobox-text';
import { DropdownList } from '../commands/list/dropdown-list';
import { ToggleBold } from '../commands/toggle-bold';
import { ToggleItalic } from '../commands/toggle-italic';
import { ToggleQuote } from '../commands/toggle-quote';
import { ToggleStrike } from '../commands/toggle-strike';
import { ToggleUnderline } from '../commands/toggle-underline';

import { ComponentType } from 'react';
import { BREAKPOINT, useMediaQuery } from '../../../hooks/use-media-query';
import { EDITOR_CONTROLS, type EditorControl } from '../controls';

interface FixedMenuProps {
  controls?: EditorControl[];
  editor: Editor;
  mediaPicker?: ComponentType<MediaPickerComponent>;
}

export function FixedMenu({ controls, editor, mediaPicker }: FixedMenuProps) {
  const isMd = useMediaQuery({ breakpoint: BREAKPOINT.md });
  const show = (control: EditorControl) => !controls || controls.includes(control);
  const showTextControls = [
    EDITOR_CONTROLS.toggleBold,
    EDITOR_CONTROLS.toggleItalic,
    EDITOR_CONTROLS.toggleStrike,
    EDITOR_CONTROLS.toggleUnderline,
    EDITOR_CONTROLS.toggleQuote,
    EDITOR_CONTROLS.buttonDivider,
  ].some(show);
  const showListControls = show(EDITOR_CONTROLS.comboboxText) || show(EDITOR_CONTROLS.dropdownList);

  return (
    <div className="flex flex-wrap gap-1 p-2">
      {show(EDITOR_CONTROLS.comboboxText) && <ComboboxText editor={editor} />}
      {show(EDITOR_CONTROLS.dropdownList) && <DropdownList editor={editor} />}
      {isMd && showTextControls ? (
        <>
          {showListControls && <Separator className="h-auto" orientation="vertical" />}
          {show(EDITOR_CONTROLS.toggleBold) && <ToggleBold editor={editor} />}
          {show(EDITOR_CONTROLS.toggleItalic) && <ToggleItalic editor={editor} />}
          {show(EDITOR_CONTROLS.toggleStrike) && <ToggleStrike editor={editor} />}
          {show(EDITOR_CONTROLS.toggleUnderline) && <ToggleUnderline editor={editor} />}
          {show(EDITOR_CONTROLS.toggleQuote) && <ToggleQuote editor={editor} />}
          {show(EDITOR_CONTROLS.buttonDivider) && <ButtonDivider editor={editor} />}
        </>
      ) : (
        showTextControls && <ComboboxCommands controls={controls} editor={editor} />
      )}
      {show(EDITOR_CONTROLS.buttonImage) && (
        <ButtonImage editor={editor} mediaPicker={mediaPicker} />
      )}
      <Separator className="h-auto" orientation="vertical" />
      <ButtonEraser editor={editor} type={ERASER_TYPE.all} />
      <ButtonHistory editor={editor} />
    </div>
  );
}
